import DesignForm from '@/components/forms/DesignForm/DesignForm'
import { RecordHistoryActionMenu } from '@/components/audit-log/RecordHistoryMenu'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import WebsiteDesignActivationAction from '@/components/websites/WebsiteDesignActivationAction'
import WebsiteDesignEditorHeader from '@/components/websites/WebsiteDesignEditorHeader'
import useWebsiteEditorFocusMode from '@/components/websites/useWebsiteEditorFocusMode'
import { usePathHelper } from '@/hooks/usePathHelper'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { Can } from '@/lib/casl'
import { albumQueries } from '@/lib/services/albums/albums.query'
import { categoryQueries } from '@/lib/services/categories/categories.query'
import {
  collectionQueries,
  designCollectionsFilters,
} from '@/lib/services/collections/collections.query'
import {
  designQueries,
  useActivateDesignMutation,
  useDefaultDesign,
  useDisableDesignScheduleMutation,
} from '@/lib/services/designs/designs.query'
import { designProductsFilters, productQueries } from '@/lib/services/products/products.query'
import { locationFilters, locationQueries } from '@/lib/services/locations/locations.query'
import { storeQueries, useStore } from '@/lib/services/stores/stores.query'
import { getErrorMessage } from '@/lib/utils'
import { createFileRoute, Link, notFound, useNavigate, useParams } from '@tanstack/react-router'
import { CalendarDays, ChevronLeft, Maximize2, Minimize2, Star, Wallpaper } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod/v4'

const websiteBuilderSectionSchema = z.enum(['brand-layout', 'pages-content'])

export const Route = createFileRoute('/_authenticated/$orgId/website/builder')({
  component: WebsiteBuilderPage,
  validateSearch: z.object({
    section: websiteBuilderSectionSchema.optional().catch('brand-layout'),
  }),
  loader: async ({ context, params }) => {
    if (!context.ability.can('view', 'designs')) throw notFound()

    const isGlobalOrg = Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)

    if (isGlobalOrg) {
      return { isReadOnly: !context.ability.can('edit', 'designs') }
    }

    await Promise.all([
      context.queryClient.ensureQueryData(designQueries.default({ organizationId: params.orgId })),
      context.queryClient.ensureQueryData(storeQueries.show({ organizationId: params.orgId })),
      context.queryClient.ensureQueryData(storeQueries.themes({ organizationId: params.orgId })),
      context.queryClient.ensureQueryData(
        collectionQueries.index({
          organizationId: params.orgId,
          filters: designCollectionsFilters,
        })
      ),
      context.queryClient.ensureQueryData(
        productQueries.index({
          organizationId: params.orgId,
          filters: designProductsFilters,
        })
      ),
      context.queryClient.ensureQueryData(categoryQueries.index({ organizationId: params.orgId })),
      context.queryClient.ensureQueryData(
        locationQueries.index({
          organizationId: params.orgId,
          filters: locationFilters().get(),
        })
      ),
      context.queryClient.ensureQueryData(albumQueries.index({ organizationId: params.orgId })),
    ])

    return { isReadOnly: !context.ability.can('edit', 'designs') }
  },
})

export function WebsiteBuilderPage() {
  const isGlobalOrg = useIsGlobalOrg()

  if (isGlobalOrg) {
    return <WebsiteBuilderGlobalNotice />
  }

  return <WebsiteBuilderWorkspace />
}

function WebsiteBuilderGlobalNotice() {
  return (
    <div className="max-w-6xl space-y-5">
      <AdminPageHeader
        icon={Wallpaper}
        title="Website Builder"
        description="Edit a florist storefront after choosing the florist account you want to manage."
      />

      <OrganizationContextNotice>
        Website Builder changes are florist-specific. Impersonate the right florist account before
        editing a live storefront.
      </OrganizationContextNotice>
    </div>
  )
}

function WebsiteBuilderWorkspace() {
  const navigate = useNavigate()
  const path = usePathHelper()
  const { orgId } = useParams({ strict: false })
  const { section } = Route.useSearch()
  const { data: defaultDesign } = useDefaultDesign()
  const { data: store } = useStore()
  const { mutateAsync: activateDesign, isPending: isActivating } = useActivateDesignMutation()
  const { mutateAsync: disableDesignSchedule, isPending: isDisablingSchedule } =
    useDisableDesignScheduleMutation()
  const { isFocusMode, toggleFocusMode } = useWebsiteEditorFocusMode()

  const activeDesignId = Number(store?.active_design_id) || null
  const activeScheduledDesignId = Number(store?.active_scheduled_design_id) || null
  const isDefaultDesignLive = Boolean(
    defaultDesign?.id &&
    !activeScheduledDesignId &&
    (!activeDesignId || activeDesignId === Number(defaultDesign.id))
  )
  const scheduledDesignBlockingActivation =
    activeScheduledDesignId && activeScheduledDesignId !== Number(defaultDesign?.id)
      ? {
          id: activeScheduledDesignId,
          name: store?.active_scheduled_design_name,
        }
      : null

  const handleWorkspaceSectionChange = (nextSection) => {
    void navigate({
      to: '/$orgId/website/builder',
      params: { orgId },
      search: { section: nextSection },
      replace: true,
      ignoreBlocker: true,
    })
  }

  const handleActivateDefaultDesign = async () => {
    try {
      await activateDesign(defaultDesign?.id)
      toast.success('Default design activated')
    } catch (error) {
      toast.error('Unable to activate default design', {
        description: getErrorMessage(error),
      })
    }
  }

  const handleOpenScheduledDesign = () => {
    if (!scheduledDesignBlockingActivation?.id) {
      return
    }

    void navigate({
      to: '/$orgId/designs/$designId',
      params: {
        orgId,
        designId: scheduledDesignBlockingActivation.id,
      },
    })
  }

  const handleDisableScheduledDesign = async () => {
    if (!scheduledDesignBlockingActivation?.id) {
      return
    }

    try {
      await disableDesignSchedule(scheduledDesignBlockingActivation.id)
      toast.success('Seasonal website update schedule turned off')
    } catch (error) {
      toast.error('Unable to turn off seasonal update', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-hidden">
      <WebsiteDesignEditorHeader
        title="Edit your default storefront"
        description="Customers see this design whenever no seasonal website update is live."
        icon={Wallpaper}
        compact={isFocusMode}
        badges={[{ label: 'Default design', variant: 'brand', icon: Star }]}
        actions={
          <>
            <AdminHeaderActionButton type="button" variant="outline" onClick={toggleFocusMode}>
              {isFocusMode ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
              {isFocusMode ? 'Exit Focus' : 'Focus'}
            </AdminHeaderActionButton>
            <AdminHeaderActionButton asChild variant="outline">
              <Link {...path.designs.index().link}>
                <ChevronLeft className="size-4" />
                Back To Designs
              </Link>
            </AdminHeaderActionButton>
            <WebsiteDesignActivationAction
              activateTitle="Activate default design?"
              activateDescription="This will make your default design live on your storefront now. Seasonal website updates can still replace it later when their schedule starts."
              disabled={!defaultDesign?.id}
              isLive={isDefaultDesignLive}
              isPending={isActivating}
              onActivate={handleActivateDefaultDesign}
              onOpenScheduledDesign={handleOpenScheduledDesign}
              scheduledDesign={scheduledDesignBlockingActivation}
            />
            {defaultDesign?.id ? (
              <RecordHistoryActionMenu
                link={path.designs.link('$designId/history', defaultDesign.id)}
                triggerLabel="More website design actions"
              />
            ) : null}
          </>
        }
      />

      {scheduledDesignBlockingActivation ? (
        <Alert variant="warning" className="shrink-0">
          <CalendarDays />
          <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <AlertTitle>A seasonal website update is live</AlertTitle>
              <AlertDescription>
                You are editing the everyday/default design. Customers are currently seeing{' '}
                {scheduledDesignBlockingActivation.name || 'a scheduled seasonal update'}.
              </AlertDescription>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Button type="button" size="sm" onClick={handleOpenScheduledDesign}>
                Open Seasonal Update
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isDisablingSchedule}
                onClick={handleDisableScheduledDesign}
              >
                Turn Off Schedule
              </Button>
            </div>
          </div>
        </Alert>
      ) : null}

      <div className="min-h-0 flex-1 overflow-hidden">
        <Can do="edit" on="stores" passThrough>
          {(canEditStore) => (
            <DesignForm
              key={`website-builder:${orgId}:${defaultDesign?.id ?? 'default'}`}
              initialData={defaultDesign}
              storeSettings={store}
              canEditStore={canEditStore}
              presentation="workspace"
              workspaceSection={section}
              onWorkspaceSectionChange={handleWorkspaceSectionChange}
            />
          )}
        </Can>
      </div>
    </div>
  )
}
